import React from 'react';
import styled from '@emotion/styled';
import { Content } from './apiService';
import CountryYear from './CountryYear';
import { Flex, Text } from './styles';
import { IconFullscreen, IconVolumeOff, IconVolumeUp } from './icons';
import Mp4Player from './Components/Widgets/WidgetManager/WidgetsPlayer/Mp4Player';
import HlsPlayer from './Components/Widgets/WidgetManager/WidgetsPlayer/LazyHlsPlayer';
import { ControlButton } from './Components/Widgets/WidgetManager/WidgetsPlayer/UI';
import { PlayerChromeApi } from './Components/Widgets/WidgetManager/WidgetsPlayer/VideoPlayerTypes';
import { VideoTrack } from './Components/Player/PlayerModels';

export default function PromotedCardVideoPreview({
  imagePath,
  content,
  teaser,
  active = false,
  title,
  description,
  isNonContentNavigation,
}: {
  imagePath?: string;
  content?: Content;
  teaser: VideoTrack | undefined;
  onHoverChange?: (id: string | null) => void;
  active?: boolean;
  title?: string;
  description?: string;
  isNonContentNavigation?: boolean;
}) {
  if (!teaser?.src) return null;

  return (
    <Outer data-active="true">
      {teaser.type === 'mp4' ? (
        <Mp4Player src={teaser.src} poster={imagePath} active={active} loop autoplay>
          {(api) => (
            <PreviewControls
              content={content}
              title={title}
              description={description}
              isNonContentNavigation={isNonContentNavigation}
              api={api}
            />
          )}
        </Mp4Player>
      ) : (
        <HlsPlayer source={teaser} poster={imagePath} active={active} loop autoplay>
          {(api) => (
            <PreviewControls
              content={content}
              title={title}
              description={description}
              isNonContentNavigation={isNonContentNavigation}
              api={api}
            />
          )}
        </HlsPlayer>
      )}
    </Outer>
  );
}

function PreviewControls({
  content,
  title,
  description,
  isNonContentNavigation,
  api,
}: {
  content?: Content;
  title?: string;
  description?: string;
  isNonContentNavigation?: boolean;
  api: PlayerChromeApi;
}) {
  const displayTitle = isNonContentNavigation ? title : title ?? content?.title;
  return (
    <Overlay>
      <Flex align="center" justify="space-between" mt="auto" fullWidth>
        <Flex data-preview-content-info="true">
          {displayTitle && (
            <Title variant="body1" as="h2" ml={1}>
              {displayTitle}
            </Title>
          )}
          {isNonContentNavigation ? (
            description && (
              <Text variant="body3" as="div" ml={1}>
                {description}
              </Text>
            )
          ) : (
            <CountryYear label {...content} hasSeparator textOnly />
          )}
        </Flex>
        <ControlsWrapper data-player-controls-wrapper="true" data-player-controls="true">
          <ControlButton type="button" onClick={(e) => api.toggleFullscreen(e)} aria-label="Fullscreen">
            <IconFullscreen />
          </ControlButton>
          <ControlButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.currentTarget.blur();
              api.toggleMute();
            }}
            aria-label={api.muted ? 'Unmute' : 'Mute'}
          >
            {api.muted ? <IconVolumeOff /> : <IconVolumeUp />}
          </ControlButton>
        </ControlsWrapper>
      </Flex>
    </Overlay>
  );
}

const Outer = styled.div(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  borderRadius: theme.radius.medium,
  zIndex: 1,
  '&[data-active="true"]': {
    zIndex: 9999,
  },
  '&[data-active="true"] video': {
    opacity: 1,
  },
  '&[data-active="true"] [data-player-controls="true"]': {
    opacity: 1,
    pointerEvents: 'auto',
  },
}));

const Overlay = styled.div(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  padding: theme.spacing(2),
  display: 'flex',
  height: '100%',
  width: '100%',
}));

const ControlsWrapper = styled(Flex)(({ theme }) => ({
  marginRight: theme.spacing(1),
  zIndex: 2,
  gap: '8px',
}));

const Title = styled(Text)(({ theme }) => ({
  fontWeight: theme.font.weight.demibold,
  ...theme.font.responsiveSize(1),
  [theme.breakpoints.up('md')]: {
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    flexShrink: 0,
  },
}));
